import { LAYOUT_SCHEMA_VERSION } from "@hq/contracts";
import { newId as defaultNewId, type NewId } from "./ids";
import type { IdOptions, LayoutNode, LayoutTree, Pane, PaneInit, Split, TabGroup } from "./types";

export function idGen(opts: IdOptions): NewId {
  return opts.newId ?? defaultNewId;
}

export function makePane(init: PaneInit, gen: NewId): Pane {
  const pane: Pane = {
    type: "pane",
    id: init.id ?? gen(),
    paneType: init.paneType,
    params: init.params ?? {},
  };
  if (init.title !== undefined) pane.title = init.title;
  return pane;
}

/** A tree whose root is a single empty tab group. */
export function emptyTree(opts: IdOptions = {}): LayoutTree {
  return { version: LAYOUT_SCHEMA_VERSION, root: { type: "tabs", id: idGen(opts)(), tabs: [] } };
}

/** A tree with one tab group holding `rootPane`, which is active and focused. */
export function createTree(rootPane: PaneInit, opts: IdOptions = {}): LayoutTree {
  const gen = idGen(opts);
  const pane = makePane(rootPane, gen);
  return {
    version: LAYOUT_SCHEMA_VERSION,
    root: { type: "tabs", id: gen(), tabs: [pane], activeTabId: pane.id },
    focusedPaneId: pane.id,
  };
}

/** Pre-order traversal. `path` is the list of node ids from the root down to `node` inclusive. */
export function walk(
  node: LayoutNode,
  visit: (node: LayoutNode, path: string[]) => void,
  path: string[] = [],
): void {
  const here = [...path, node.id];
  visit(node, here);
  if (node.type === "split") for (const child of node.children) walk(child, visit, here);
}

export function findNode(tree: LayoutTree, id: string): LayoutNode | undefined {
  let found: LayoutNode | undefined;
  walk(tree.root, (node) => {
    if (found === undefined && node.id === id) found = node;
  });
  return found;
}

export type PaneLocation = {
  pane: Pane;
  group: TabGroup;
  /** Node ids from the root down to the group, inclusive. */
  path: string[];
};

export function findPane(tree: LayoutTree, paneId: string): PaneLocation | undefined {
  let found: PaneLocation | undefined;
  walk(tree.root, (node, path) => {
    if (found !== undefined || node.type !== "tabs") return;
    const pane = node.tabs.find((tab) => tab.id === paneId);
    if (pane) found = { pane, group: node, path };
  });
  return found;
}

/** The split that directly contains the node with `id`; undefined for the root. */
export function parentOf(tree: LayoutTree, id: string): Split | undefined {
  let found: Split | undefined;
  walk(tree.root, (node) => {
    if (found === undefined && node.type === "split" && node.children.some((c) => c.id === id)) {
      found = node;
    }
  });
  return found;
}

export function allGroups(tree: LayoutTree): TabGroup[] {
  const groups: TabGroup[] = [];
  walk(tree.root, (node) => {
    if (node.type === "tabs") groups.push(node);
  });
  return groups;
}

export function allPanes(tree: LayoutTree): Pane[] {
  return allGroups(tree).flatMap((group) => group.tabs);
}

export function firstGroup(node: LayoutNode): TabGroup | undefined {
  if (node.type === "tabs") return node;
  for (const child of node.children) {
    const group = firstGroup(child);
    if (group) return group;
  }
  return undefined;
}

export function lastGroup(node: LayoutNode): TabGroup | undefined {
  if (node.type === "tabs") return node;
  for (let i = node.children.length - 1; i >= 0; i--) {
    const child = node.children[i];
    const group = child ? lastGroup(child) : undefined;
    if (group) return group;
  }
  return undefined;
}

/** The group's active tab, falling back to its first tab. */
export function activePane(group: TabGroup): Pane | undefined {
  return group.tabs.find((tab) => tab.id === group.activeTabId) ?? group.tabs[0];
}
